<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { usePrintTemplates } from '@/composables/print-template'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import PrintTemplateItem from '@/components/accounting/PrintTemplateItem.vue'
import PrintTemplateThumbnail from '@/components/accounting/PrintTemplateThumbnail.vue'

const props = defineProps({
  modelValue: {
    type: Number,
    required: false,
    default: null,
  },
  documentType: {
    type: Number,
    required: true,
  },
  label: {
    type: String,
    required: false,
    default: null,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { templateById, selectableTemplates } = usePrintTemplates()

const showMobilePicker = ref(false)
const options = computed(() => selectableTemplates(props.modelValue))
const selected = computed(() => templateById(props.modelValue))
const fieldLabel = computed(() => props.label ?? i18n.t('common.printTemplate'))

const onSelect = (id) => {
  showMobilePicker.value = false
  emit('update:modelValue', id)
}

const onFieldClick = () => {
  if (!props.readonly) showMobilePicker.value = true
}
</script>
<template>
  <div class="row no-wrap items-center">
    <div v-if="isMobile" class="col" @click="onFieldClick">
      <q-field
        :model-value="modelValue"
        :label="fieldLabel"
        :readonly="readonly"
        stack-label
      >
        <template #control>
          <span v-if="selected">{{ selected.name }}</span>
        </template>
        <template #append>
          <q-icon :name="readonly ? 'mdi-lock-outline' : 'mdi-menu-down'" />
        </template>
      </q-field>
    </div>
    <q-select
      v-else
      class="col"
      :model-value="modelValue"
      :options="options"
      :label="fieldLabel"
      :readonly="readonly"
      option-value="id"
      option-label="name"
      emit-value
      map-options
      @update:model-value="onSelect"
    >
      <template #option="scope">
        <q-item v-bind="scope.itemProps">
          <print-template-item :template="scope.opt" :document-type="documentType" />
        </q-item>
      </template>
      <template #after-options>
        <q-item dense class="text-caption text-grey-7">
          <q-item-section avatar>
            <q-icon name="mdi-information-outline" size="xs" />
          </q-item-section>
          <q-item-section>{{ i18n.t('info.printTemplatesSelectHint') }}</q-item-section>
        </q-item>
      </template>
    </q-select>
    <print-template-thumbnail
      v-if="selected"
      class="print-template-select-preview q-ml-md"
      :template="selected"
      :document-type="documentType"
    />

    <q-dialog
      :model-value="showMobilePicker"
      maximized
      @hide="showMobilePicker = false"
    >
      <q-card class="dialog-card-flex">
        <navigate-back :text="fieldLabel" @cancel="showMobilePicker = false" />
        <q-card-section class="scroll col q-pa-sm">
          <q-list>
            <q-item
              v-for="template in options"
              :key="template.id"
              clickable
              :active="template.id === modelValue"
              @click="onSelect(template.id)"
            >
              <print-template-item :template="template" :document-type="documentType" />
            </q-item>
          </q-list>
          <div class="text-caption text-grey-7 q-pa-md">
            {{ i18n.t('info.printTemplatesSelectHint') }}
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>
<style scoped>
.print-template-select-preview {
  flex-shrink: 0;
  width: calc(2.75rem * 210 / 297);
}
</style>
